import LegalLayout, { LegalList, Section } from "@/components/LegalLayout";
import { CONTROLLER_ADDRESS, CONTROLLER_NAME, PRIVACY_CONTACT_EMAIL } from "@/lib/legal";

export default function PrivacyPage() {
  return (
    <LegalLayout eyebrow="Legal" title="Privacy Notice" updated="22 July 2026">
      <Section title="1. Who is responsible">
        <p>
          The controller for Callsect account administration and private-beta operations is {CONTROLLER_NAME}, {CONTROLLER_ADDRESS}. Privacy and
          support requests can be sent to <a className="text-indigo-300 hover:text-indigo-200" href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</a>.
        </p>
        <p>Callsect is currently operated as a limited private beta for professional users.</p>
        <p>
          When a professional user uploads a work call on behalf of an employer or another organisation,
          that organisation may be the controller for the call content and Callsect may act as its service
          provider or processor. The user and their organisation must determine the correct roles, lawful
          instructions and participant notices before uploading recordings. This notice does not itself
          create a data processing agreement with an organisation.
        </p>
      </Section>

      <Section title="2. Data we process">
        <LegalList>
          <li>Account data: name, email address, password hash, account type and legal-acceptance records.</li>
          <li>Call data: title, customer or company label, call date, transcript, speaker segments, duration and transcription metadata.</li>
          <li>Generated data: CRM notes, findings, dissection, coaching, relationship timelines and Writing DNA profiles.</li>
          <li>Operational data: beta quota counters, rate-limit records and limited technical request information.</li>
        </LegalList>
        <p>
          Account and upload data come directly from the user. Transcripts and generated results are derived
          from the uploaded recording. Account credentials and the information required to process a call are
          necessary to provide the service. Profile labels, Writing DNA and optional metadata are voluntary.
        </p>
      </Section>

      <Section title="3. Audio and transcript lifecycle">
        <ol className="list-decimal space-y-3 pl-5 marker:text-indigo-400">
          <li>The backend writes uploaded audio to temporary server storage for validation.</li>
          <li>The audio is sent to AssemblyAI&apos;s EU API endpoint for transcription, automatic language detection, speaker labels and timestamps.</li>
          <li>After Callsect retrieves the result, it requests immediate deletion of the AssemblyAI transcript and the uploaded audio associated with it.</li>
          <li>The temporary server file is deleted after the transcription attempt, including failure paths.</li>
          <li>The resulting transcript, not the original audio, is sent to OpenAI&apos;s API to generate findings, coaching, CRM notes and relationship memory.</li>
        </ol>
        <p>
          The original recording is not stored in the Callsect database. If an AssemblyAI upload succeeds but
          no transcript identifier is created, or if a remote deletion request fails, immediate deletion cannot
          be confirmed and the provider&apos;s standard retention rules may apply. Such failures are recorded in
          backend logs for operational follow-up.
        </p>
      </Section>

      <Section title="4. Why we use the data">
        <LegalList>
          <li>To provide requested transcription, analysis, CRM-note, coaching and relationship-memory features.</li>
          <li>To authenticate users, secure the service and prevent abuse.</li>
          <li>To administer quotas and operate the private beta.</li>
          <li>To meet legal obligations and establish or defend legal claims where necessary.</li>
        </LegalList>
        <p>
          The principal legal bases are performance of the private-beta agreement (Article 6(1)(b) GDPR),
          legitimate interests in operating and securing the service and preventing abuse (Article 6(1)(f)),
          and compliance with legal obligations where applicable (Article 6(1)(c)). Consent is relied on only
          where the relevant law requires it.
        </p>
        <p>
          Where consent or notice is required for recording or processing a conversation, the uploader and
          their organisation are responsible for satisfying those requirements before upload. The in-app
          confirmation records the uploader&apos;s representation of authority; it does not prove that every
          legal, employment, confidentiality or contractual requirement has been met.
        </p>
      </Section>

      <Section title="5. Service providers and international processing">
        <LegalList>
          <li>Render: application hosting and temporary server-side audio processing.</li>
          <li>MongoDB Atlas: storage of account data, transcripts, metadata and generated results.</li>
          <li>AssemblyAI: audio transcription, language detection, speaker diarization and timestamps through the configured EU API endpoint.</li>
          <li>OpenAI: analysis and generation from transcript text; the original audio is not sent to OpenAI in the current implementation.</li>
        </LegalList>
        <p>
          Providers may use subprocessors and may process limited account, usage, security or support data
          outside Germany or the EEA under their current contractual transfer safeguards. Use of an EU API
          endpoint does not remove the need to review the provider&apos;s DPA, subprocessors and international-transfer arrangements.
        </p>
        <p>
          Callsect does not intentionally submit call content for provider model training. OpenAI states that
          API inputs and outputs are not used for training by default unless the API customer opts in. Callsect
          sends audio to AssemblyAI through its European endpoint; AssemblyAI states that files submitted through
          its European servers are not used for model training. A change away from that endpoint requires a fresh
          legal and notice review.
        </p>
      </Section>

      <Section title="6. Retention and deletion">
        <LegalList>
          <li>Saved account and call data remain until the user deletes the relevant call or account.</li>
          <li>Callsect requests deletion of the AssemblyAI transcript and associated uploaded audio immediately after retrieving the transcription result.</li>
          <li>The temporary Render-side audio file is deleted after the transcription attempt.</li>
          <li>OpenAI states that default API abuse-monitoring logs may retain customer content for up to 30 days, subject to legal exceptions and any approved account-level retention controls.</li>
          <li>Short-lived security and rate-limit records expire automatically or are deleted with the account.</li>
          <li>Account-unlinked aggregate beta counters may remain for spend-control integrity.</li>
          <li>Infrastructure logs and backups may follow provider-configured retention periods.</li>
        </LegalList>
        <p>
          Users can delete individual calls, export their account data as JSON and permanently delete their
          account in the app. Deleting a call removes its saved Callsect transcript, analysis, coaching and CRM
          notes and updates or removes the related customer-memory record.
        </p>
      </Section>

      <Section title="7. Your rights">
        <p>
          Subject to applicable law, you may request access, correction, deletion, restriction, portability
          or object to processing. You may withdraw consent where consent is the legal basis, without affecting
          earlier lawful processing. Requests should be sent to <a className="text-indigo-300 hover:text-indigo-200" href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</a>.
        </p>
        <p>
          You may also complain to a competent data-protection authority. For a private-sector controller
          established in Bavaria, the relevant authority is generally the Bavarian State Office for Data
          Protection Supervision (BayLDA).
        </p>
      </Section>

      <Section title="8. Automated analysis and security">
        <p>
          Callsect generates probabilistic AI-assisted observations and coaching. They may be incomplete or
          wrong and are not used by Callsect to make legal or similarly significant decisions about people.
          Passwords are stored as hashes, access is authenticated, and stored user data is scoped to the
          signed-in account. No internet service can guarantee absolute security.
        </p>
      </Section>

      <Section title="9. Changes">
        <p>
          Material changes require a new legal version. Existing users must review and accept the current
          Privacy Notice and Private Beta Terms before returning to protected features.
        </p>
      </Section>
    </LegalLayout>
  );
}
